import { RuleApiModel } from '../../../../lib/api/rulemanager/types';
import { Actions } from './Actions';
import { ActionType } from './ActionType';
import { Comparisons } from './Comparisons';
import { Conditions } from './Conditions';
import { Operators } from './Operators';
import { SortFilterCollator } from './SortFilterCollator';
import { uid } from './uid';

export class RuleNames {
  static DomesticService = 'Domestic Service';
  static DomesticCourierService = 'Domestic Business Service';
  static LabelReference = 'Label Reference';
  static InternationalService = 'International Service';
  static InternationalCourierService = 'International Business Service';
  static Email = 'Email';
  static Phone = 'Phone';
  static Extras = 'Extras';
  static Weight = 'Weight';
  static MarketplaceServices = 'Marketplace services';
  static Dimensions = 'Dimensions';
  static Tag = 'Default Rule';
}

export function generateDefaultDomesticServiceRule(serviceSlug: string, domesticIsoCode: string): RuleApiModel {
  return {
    tags: [RuleNames.Tag],
    actions: [
      {
        field: Actions.Item_Service,
        type: ActionType.Replace,
        value: serviceSlug,
        id: uid()
      }
    ],
    active: true,
    breakOnConditionMet: false,
    conditionType: Operators.Or,
    conditions: [
      {
        comparison: Comparisons.EqualTo,
        field: Conditions.Recipient_Country,
        values: [domesticIsoCode],
        id: uid()
      }
    ],
    priority: 0,
    name: RuleNames.DomesticService,
    description: 'Applies the selected service for all of your domestic items, where available.'
  } as RuleApiModel;
}

export function generateDefaultDomesticCourierServiceRule(serviceSlug: string, domesticIsoCode: string): RuleApiModel {
  return {
    tags: [RuleNames.Tag],
    actions: [
      {
        field: Actions.Item_CourierService,
        type: ActionType.Replace,
        value: serviceSlug,
        id: uid()
      }
    ],
    active: true,
    breakOnConditionMet: false,
    conditionType: Operators.Or,
    conditions: [
      {
        comparison: Comparisons.EqualTo,
        field: Conditions.Recipient_Country,
        values: [domesticIsoCode],
        id: uid()
      }
    ],
    priority: 0,
    name: RuleNames.DomesticCourierService,
    description: 'Applies the selected service for all of your domestic items, where available.'
  } as RuleApiModel;
}

export function generateDefaultLabelReferenceRule(
  marketplaceSource: string,
  labelType: string,
  customValue: string
): RuleApiModel {
  const actions = customValue
    ? [
        {
          field: Actions.Label_ReferenceValue,
          type: ActionType.Replace,
          value: customValue,
          id: uid()
        },
        {
          field: Actions.Label_Reference,
          type: ActionType.Replace,
          value: labelType,
          id: uid()
        }
      ]
    : [
        {
          field: Actions.Label_Reference,
          type: ActionType.Replace,
          value: labelType,
          id: uid()
        }
      ];

  return {
    tags: [RuleNames.Tag, 'Shipping'],
    actions: actions,
    active: true,
    breakOnConditionMet: false,
    conditionType: Operators.Or,
    conditions: [
      {
        comparison: Comparisons.EqualTo,
        field: Conditions.Item_Source,
        values: [marketplaceSource],
        id: uid()
      }
    ],
    priority: 0,
    name: RuleNames.LabelReference,
    description: `Choose which fields gets displayed on your label for '${marketplaceSource}'.`
  } as RuleApiModel;
}

export function generateMarketplaceServiceRule(
  serviceSlug: string,
  serviceName: string,
  marketplaceServiceSlug: string,
  marketplaceServiceName: string,
  marketplaceSlug: string
): RuleApiModel {
  return {
    tags: [RuleNames.Tag],
    actions: [
      {
        field: Actions.Item_Service,
        type: ActionType.Replace,
        value: serviceSlug,
        id: uid()
      }
    ],
    active: true,
    breakOnConditionMet: false,
    conditionType: Operators.And,
    conditions: [
      {
        comparison: Comparisons.EqualTo,
        field: Conditions.Item_MarketplaceService,
        values: [marketplaceServiceSlug],
        id: uid()
      },
      {
        comparison: Comparisons.IsOneOf,
        field: Conditions.Item_Source,
        values: [marketplaceSlug],
        id: uid()
      }
    ],
    priority: 0,
    name: RuleNames.MarketplaceServices,
    description: `Applies '${serviceName}' to '${marketplaceServiceName}' for '${marketplaceSlug}' services.`
  } as RuleApiModel;
}

export function generateDefaultInternationalServiceRule(serviceSlug: string, domesticIsoCode: string): RuleApiModel {
  return {
    tags: [RuleNames.Tag],
    actions: [
      {
        field: Actions.Item_Service,
        type: ActionType.Replace,
        value: serviceSlug,
        id: uid()
      }
    ],
    active: true,
    breakOnConditionMet: false,
    conditionType: Operators.Or,
    conditions: [
      {
        comparison: Comparisons.NotEqualTo,
        field: Conditions.Recipient_Country,
        values: [domesticIsoCode],
        id: uid()
      }
    ],
    priority: 0,
    name: RuleNames.InternationalService,
    description: 'Applies the selected service for all of your international items, where available.'
  } as RuleApiModel;
}

export function generateDefaultInternationalCourierServiceRule(
  serviceSlug: string,
  domesticIsoCode: string
): RuleApiModel {
  return {
    tags: [RuleNames.Tag],
    actions: [
      {
        field: Actions.Item_CourierService,
        type: ActionType.Replace,
        value: serviceSlug,
        id: uid()
      }
    ],
    active: true,
    breakOnConditionMet: false,
    conditionType: Operators.Or,
    conditions: [
      {
        comparison: Comparisons.NotEqualTo,
        field: Conditions.Recipient_Country,
        values: [domesticIsoCode],
        id: uid()
      }
    ],
    priority: 0,
    name: RuleNames.InternationalCourierService,
    description: 'Applies the selected service for all of your international items, where available.'
  } as RuleApiModel;
}

export function generateDefaultEmailRule(email: string): RuleApiModel {
  return {
    tags: [RuleNames.Tag],
    actions: [
      {
        field: Actions.Recipient_Email,
        type: ActionType.Replace,
        value: email,
        id: uid()
      }
    ],
    active: true,
    breakOnConditionMet: false,
    conditionType: Operators.Or,
    conditions: [
      {
        comparison: Comparisons.IsEmpty,
        field: Conditions.Recipient_Email,
        values: [],
        id: uid()
      }
    ],
    priority: 0,
    name: RuleNames.Email,
    description: 'Applies the selected email where one is missing.'
  } as RuleApiModel;
}

export function generateDefaultPhoneRule(phone: string): RuleApiModel {
  return {
    tags: [RuleNames.Tag],
    actions: [
      {
        field: Actions.Recipient_Phone,
        type: ActionType.Replace,
        value: phone,
        id: uid()
      }
    ],
    active: true,
    breakOnConditionMet: false,
    conditionType: Operators.Or,
    conditions: [
      {
        comparison: Comparisons.IsEmpty,
        field: Conditions.Recipient_Phone,
        values: [],
        id: uid()
      }
    ],
    priority: 0,
    name: RuleNames.Phone,
    description: 'Applies the selected phone number where one is missing.'
  } as RuleApiModel;
}

export function generateDefaultWeightRule(weight: number): RuleApiModel {
  return {
    tags: [RuleNames.Tag],
    actions: [
      {
        field: Actions.Item_Weight,
        type: ActionType.Replace,
        value: String(weight),
        id: uid()
      }
    ],
    active: true,
    breakOnConditionMet: false,
    conditionType: Operators.And,
    conditions: [
      {
        comparison: Comparisons.LessThanOrEqualTo,
        field: Conditions.Item_Weight,
        values: ['0'],
        id: uid()
      }
    ],
    priority: 0,
    name: RuleNames.Weight,
    description: 'Applies a default weight when no weight is imported.'
  } as RuleApiModel;
}

export function generateDefaultDimensionsRule(height: number, width: number, length: number): RuleApiModel {
  const dims = [height, width, length].map(String).sort(SortFilterCollator.compare).reverse();
  return {
    tags: [RuleNames.Tag],
    actions: [
      {
        field: Actions.Item_Length,
        type: ActionType.Replace,
        value: dims[0],
        id: uid()
      },
      {
        field: Actions.Item_Width,
        type: ActionType.Replace,
        value: dims[1],
        id: uid()
      },
      {
        field: Actions.Item_Height,
        type: ActionType.Replace,
        value: dims[2],
        id: uid()
      }
    ],
    active: true,
    breakOnConditionMet: false,
    conditionType: Operators.And,
    conditions: [
      {
        comparison: Comparisons.LessThanOrEqualTo,
        field: Conditions.Item_Length,
        values: ['0'],
        id: uid()
      },
      {
        comparison: Comparisons.LessThanOrEqualTo,
        field: Conditions.Item_Width,
        values: ['0'],
        id: uid()
      },
      {
        comparison: Comparisons.LessThanOrEqualTo,
        field: Conditions.Item_Height,
        values: ['0'],
        id: uid()
      }
    ],
    priority: 0,
    name: RuleNames.Dimensions,
    description: 'Applies default dimensions when no dimensions are imported.'
  } as RuleApiModel;
}

export function generateDefaultExtrasRule(extras: string[]): RuleApiModel {
  const rule = {
    tags: [RuleNames.Tag],
    actions: [],
    active: true,
    breakOnConditionMet: false,
    conditionType: Operators.Or,
    conditions: [],
    priority: 0,
    name: RuleNames.Extras,
    description: 'Applies the selected extras for all of your items, where available.'
  } as RuleApiModel;

  extras.forEach((field) => {
    rule.actions.push({
      field: field,
      type: ActionType.Replace,
      value: 'true',
      id: uid()
    });
  });

  return rule;
}
